import { LoadingSpinner as BaseLoadingSpinner, FullPageError } from "./common/components";
import { BookingData, Service } from "./AppDataProvider/AppDataProvider";
import { isFeatureFlagEnabledForOrganisation, useClientLocation } from "./utils";
import React, { ReactElement } from "react";
import { absoluteCentre } from "./common/styles";
import { ContentContainer } from "./ContentContainer";
import styled from "styled-components";
import { useGetBookingToolDataQuery } from "./GeneratedGraphQL";
type BookingToolDataProps = {
  inPopoverMode?: boolean;
};
export function BookingToolData({
  inPopoverMode
}: BookingToolDataProps): ReactElement {
  const {
    location,
    timezone: clientTimezone
  } = useClientLocation({
    fallbackLocation: "GB",
    fallbackTimezone: "Europe/London"
  });
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id") ?? "";
  const preFillResourceId = urlParams.get("resourceId");
  const createdSource = urlParams.get("createdSource");
  const prefilledOrganisationId = urlParams.get("organisationId");
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetBookingToolDataQuery({
    variables: {
      id,
      serviceFilters: {
        ...(preFillResourceId && {
          resourceIds: [preFillResourceId]
        }),
        /**
         * We want to show invisible services for constructed booking tools that
         * have _only_ invisible services assigned to them.
         */
        showInvisibleIfOnlyInvisibleSelected: true,
        visibleOnly: true
      }
    }
  });
  const services = data?.bookingTool?.services;
  const organisations = data?.bookingTool?.organisations;
  const forceServiceSelection = data?.bookingTool?.forceServiceSelection;
  const organisationAvailabilityFlow = data?.bookingTool?.organisationAvailabilityFlow;
  if (loading) return <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>;
  if (error || !data?.bookingTool || !organisations?.edges.length) return <FullPageError />;
  if (services && (services.pageInfo.hasNextPage || organisations.pageInfo.hasNextPage)) {
    void fetchMore({
      variables: {
        id,
        ...(organisations.pageInfo.hasNextPage && {
          organisationCursor: organisations.pageInfo.endCursor
        }),
        ...(services.pageInfo.hasNextPage && {
          serviceCursor: services.pageInfo.endCursor
        })
      }
    });
  }

  /**
   * It's possible we are still fetching the next pages
   * of the organisations & services graphs.
   *
   * If that is the case return the loading content
   */
  if (services && (services.pageInfo.hasNextPage || organisations.pageInfo.hasNextPage)) {
    return <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>;
  }

  /**
   * There is a scenario where timezone.name can be undefined: Sentry issue https://appointedd-eu.sentry.io/issues/1474580/
   *
   * For now, adding a fallback timezone to fix this in case of undefined
   */

  let bookingData: BookingData = {
    createdSource: createdSource ? createdSource : "bookingapp",
    sessionId: Math.random().toString(12).substring(2, 9),
    timezone: clientTimezone ?? "Europe/London"
  };

  // If there is only 1 organisation we need to automatically select this organisation
  if (organisations.edges.length === 1) {
    const organisation = organisations.edges[0].node;
    bookingData = {
      ...bookingData,
      organisation: organisation,
      ...(organisation.forceTimezone !== "AUTO_DETECT" && {
        timezone: organisation.forceTimezone
      })
    };
  }
  const prefilledOrganisation = prefilledOrganisationId && organisations.edges ? organisations.edges.find(({
    node
  }) => node._id === prefilledOrganisationId) : null;

  /**
   * Set organisation and service: when there is a prefilled organisation, set the organisation to that pre-filled organisation
   * Filter services to find ones that include that prefilled organisation; then take the first service (for Good Feet there is only one service per organisation)
   * This IF block will contain the GF feature flag that will be replaced with a booking tool feature flag later
   */
  if (prefilledOrganisationId && prefilledOrganisation && organisationAvailabilityFlow && services && services.edges.length > 0) {
    const organisationServices = services.edges.filter(({
      node
    }) => node.organisation._id === prefilledOrganisationId);
    const service = (organisationServices[0].node as Service);
    const {
      durations,
      maxParticipantsPerBooking
    } = service;
    bookingData = {
      ...bookingData,
      organisation: prefilledOrganisation.node,
      service,
      ...(durations.length === 1 && {
        duration: durations[0]
      }),
      ...((maxParticipantsPerBooking ?? 1) === 1 && {
        attendees: 1
      })
    };
  }

  /**
   * If there is only 1 service we need to use the id of that single service.
   * This will be required when the service selection step is disabled.
   *
   * Also, if the service only allows one attendee, or only has one duration,
   * we also need to add those values directly to the bookingData.
   */
  if (services && services.edges.length === 1 && !forceServiceSelection) {
    const service = (services.edges[0].node as Service);
    const {
      durations,
      maxParticipantsPerBooking
    } = service;
    bookingData = {
      ...bookingData,
      service,
      ...(durations.length === 1 && {
        duration: durations[0]
      }),
      ...((maxParticipantsPerBooking ?? 1) === 1 && {
        attendees: 1
      })
    };
  }
  return <ContentContainer bookingData={bookingData} bookingTool={data.bookingTool} inPopoverMode={inPopoverMode} userLocation={location} data-sentry-element="ContentContainer" data-sentry-component="BookingToolData" data-sentry-source-file="BookingToolData.tsx" />;
}
const LoadingSpinner = styled(BaseLoadingSpinner)`
  ${absoluteCentre}
`;
const LoadingSpinnerContainer = styled.section`
  min-height: 550px;
`;
export default BookingToolData;